import React, { useState, useEffect, useCallback, useRef, useMemo } from "react"
import { useWatch } from "antd/es/form/Form"
import { debounce } from "lodash"
import PropTypes from "prop-types"
import { Form, Input, Row, Col, Checkbox, Select, Spin } from "antd"
import { useTranslation } from "react-i18next"
import AlreadyLinkedNotice from "./AlreadyLinkedNotice"
import { PhoneInput } from "ui/Input"
import { getExistingClientDetail, getExistingClientListing, cleanedExistingClientList, setClientId, cleanClientId } from "store/bookings"
import { setSelectedPaymentMethodId, cleanedPaymentMethods } from "store/user"
import { useDispatch, useSelector } from "react-redux"
import { collectionSelector } from "store/selectors"
import { useLocation } from "react-router-dom"
import { handleNameKeyDown } from "../bookingUtils"
import { validatePhoneNumber } from "ui/Input/PhoneInput"
import { handleLastNameKeyPress, handleLastNameInput, handleLastNameBlur } from "pagesV2/pro/utils/ProUtils"

const ClientDetail = ({ form, bookingId, setAlertMessage, setExistingClientId }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [loading, setLoading] = useState(false)
  const [emailError, setEmailError] = useState(null)
  const [phoneError, setPhoneError] = useState(null)
  const [emailMatchedClient, setEmailMatchedClient] = useState(null)
  const [phoneMatchedClient, setPhoneMatchedClient] = useState(null)
  const existingClientsList = useSelector(collectionSelector("bookings.existing_clients_list"))
  const email = useWatch("email", form)
  const isExistingClient = useWatch("isExistingClient", form)
  const lastChangedField = useRef(null)
  const clientId = useSelector((state) => state.bookings.clientId)
  const location = useLocation()
  const clientUserId = location?.state?.client?.user_id
  const phone = useWatch("phone_number", form)
  const clientDetailDisabled = !!bookingId || !!clientUserId

  // Derived: conflict when email and phone match two different clients
  const conflictClients = useMemo(() => {
    if (emailMatchedClient && phoneMatchedClient && emailMatchedClient.user_id !== phoneMatchedClient.user_id) {
      return [emailMatchedClient, phoneMatchedClient]
    }
    return null
  }, [emailMatchedClient, phoneMatchedClient])

  const singleMatchedClient = conflictClients ? null : emailMatchedClient || phoneMatchedClient
  const singleMatchType = !conflictClients && (emailMatchedClient && phoneMatchedClient ? "both" : emailMatchedClient ? "email" : phoneMatchedClient ? "phone" : null)
  const showNotice = !isExistingClient && !!(emailMatchedClient || phoneMatchedClient)

  const clearMatchedClients = useCallback(() => {
    setEmailMatchedClient(null)
    setPhoneMatchedClient(null)
    setEmailError(null)
    setPhoneError(null)
    form.setFields([
      { name: "email", errors: [] },
      { name: "phone_number", errors: [] }
    ])
  }, [form])

  // --- PHONE FIELD STATE ---
  const [phoneDisabled, setPhoneDisabled] = useState(false)
  useEffect(() => {
    // Initial load: new client
    if (!isExistingClient && !bookingId && !clientUserId && !clientId) {
      setPhoneDisabled(false)
      return
    }
    // Existing client checked, but no client selected
    if (isExistingClient && !clientId && !clientUserId && !bookingId) {
      setPhoneDisabled(true)
      return
    }

    if (clientDetailDisabled && email && clientUserId && !phone) {
      setPhoneDisabled(false)
      return
    }

    // Existing client selected (via dropdown or bookingId/clientUserId)
    if ((clientId || clientUserId) && isExistingClient) {
      const client = existingClientsList?.find((c) => c.user_id === clientId || c.user_id === clientUserId)
      if (client) {
        const phoneValue = client.phone_number ? { fullNumber: client.phone_number, countryCode: client.country_code } : null
        setPhoneDisabled(!!client.phone_number)
        form.setFieldsValue({ phone_number: phoneValue })
      }
      return
    }

    // Fallback: use form value
    setPhoneDisabled(clientDetailDisabled || isExistingClient)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExistingClient, clientId, clientUserId, existingClientsList, email, clientDetailDisabled])

  const onEmailInputChange = (e) => {
    let email = e.target.value.replaceAll(" ", "") // Remove all spaces
    form.setFieldsValue({ email })
    lastChangedField.current = "email"
    if (clientId && email) {
      dispatch(cleanClientId())
      dispatch(cleanedPaymentMethods())
      dispatch(setSelectedPaymentMethodId(""))
    }
  }

  const handleExistingClientSelect = (e) => {
    setLoading(true)
    const value = e.target.checked
    if (value) {
      // Existing client checked: clear and disable phone synchronously
      setPhoneDisabled(true)
      clearMatchedClients()
      form.setFieldsValue({ phone_number: null }) // clear phone first
      form.setFieldsValue({ isExistingClient: value, email: "" })
      dispatch(getExistingClientListing())
        .then(() => {
          setLoading(false)
        })
        .catch(() => {
          setLoading(false) // Ensure loading stops on error too
        })
    } else {
      // Existing client unchecked
      setPhoneDisabled(false)
      clearMatchedClients()
      dispatch(cleanedPaymentMethods())
      dispatch(cleanedExistingClientList())
      dispatch(cleanClientId())
      form.setFieldsValue({
        existing_client: null,
        email: "",
        phone_number: null,
        existing_client_id: "",
        isExistingClient: value
      })
    }
  }

  const handleClientChange = (item) => {
    setExistingClientId(item?.value)
    const client = existingClientsList?.find((c) => c.user_id === item?.value)
    const phoneValue = client.phone_number ? { fullNumber: client.phone_number, countryCode: client.country_code } : null
    if (phoneValue) {
      form.setFieldsValue({
        email: client.email,
        phone_number: phoneValue,
        existing_client: { ...item, phone_number: phoneValue }
      })
    } else {
      form.resetFields(["phone_number"]) // clears old object value
      form.setFieldsValue({
        email: client.email,
        phone_number: null,
        existing_client: { ...item, phone_number: null }
      })
    }

    if (client?.phone_number) {
      setPhoneDisabled(true)
    } else {
      setPhoneDisabled(false)
    }
    setAlertMessage("")
    dispatch(setClientId(item?.value))
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedValidateEmail = useCallback(
    debounce(async (emailVal) => {
      if (!emailVal?.includes(".")) {
        setEmailError(null)
        setEmailMatchedClient(null)
        setAlertMessage("")
        form.setFields([{ name: "email", errors: [] }])
        return
      }
      const res = await dispatch(getExistingClientDetail({ q: emailVal }))
      const matched = Array.isArray(res) ? res[0] : res
      if (matched?.id) {
        setEmailMatchedClient(matched)
        setEmailError(t("booking.error_messages.email_already_exists"))
        setAlertMessage(t("booking.email_already_exists"))
        form.setFields([{ name: "email", errors: [t("booking.error_messages.email_already_exists")] }])
      } else {
        setEmailMatchedClient(null)
        setEmailError(null)
        setAlertMessage("")
        form.setFields([{ name: "email", errors: [] }])
      }
    }, 800),
    [dispatch, t, form]
  )

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedValidatePhone = useCallback(
    debounce(async (phoneVal) => {
      try {
        await validatePhoneNumber(phoneVal)
      } catch {
        return
      }
      const res = await dispatch(getExistingClientDetail({ q: phoneVal }))
      const matched = Array.isArray(res) ? res[0] : res
      if (matched?.id) {
        setPhoneMatchedClient(matched)
        setPhoneError(t("booking.error_messages.phone_already_exists"))
        form.setFields([{ name: "phone_number", errors: [t("booking.error_messages.phone_already_exists")] }])
      } else {
        setPhoneMatchedClient(null)
        setPhoneError(null)
        form.setFields([{ name: "phone_number", errors: [] }])
      }
    }, 800),
    [dispatch, t, form]
  )

  useEffect(() => {
    return () => {
      debouncedValidateEmail.cancel()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    return () => {
      debouncedValidatePhone.cancel()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (isExistingClient) {
      setAlertMessage("")
      setEmailError(null)
      clearMatchedClients()
      form.setFields([{ name: "email", errors: [] }])
      return
    }
    setAlertMessage("")
    if (!email?.trim()) {
      debouncedValidateEmail.cancel()
      setEmailError(null)
      setEmailMatchedClient(null)
      form.setFields([{ name: "email", errors: [] }])
      return
    }
    if (!bookingId && !clientUserId) {
      debouncedValidateEmail(email.trim())
    }
  }, [email, isExistingClient, bookingId, clientUserId, debouncedValidateEmail, form, setAlertMessage, clearMatchedClients])

  // Phone duplicate check
  useEffect(() => {
    if ((isExistingClient && phoneDisabled) || bookingId || clientUserId) {
      debouncedValidatePhone.cancel()
      setPhoneMatchedClient(null)
      setPhoneError(null)
      form.setFields([{ name: "phone_number", errors: [] }])
      return
    }
    const phoneVal = phone?.fullNumber
    if (phoneVal) {
      debouncedValidatePhone(phoneVal)
    } else {
      debouncedValidatePhone.cancel()
      setPhoneMatchedClient(null)
      setPhoneError(null)
      form.setFields([{ name: "phone_number", errors: [] }])
    }
  }, [phone, isExistingClient, phoneDisabled, bookingId, clientUserId, debouncedValidatePhone, form])

  return (
    <>
      {!bookingId && !clientUserId && (
        <Form.Item name="isExistingClient" valuePropName="checked">
          <Checkbox onChange={handleExistingClientSelect}>{t("booking.existing_client")}</Checkbox>
        </Form.Item>
      )}
      <Row gutter={16} style={{ marginTop: 16 }}>
        {isExistingClient ? (
          <Col xs={24} sm={24}>
            <Form.Item
              name="existing_client"
              label={t("booking.labels.existing_client")}
              rules={[{ required: true, message: t("booking.error_messages.existing_client_required") }]}
            >
              <Select
                labelInValue
                placeholder="Select client"
                showSearch
                filterOption={(input, option) => option.label.toLowerCase().includes(input.toLowerCase())}
                options={existingClientsList?.map((client) => ({
                  label: `${client.first_name} ${client.last_name}`,
                  value: client.user_id
                }))}
                onChange={handleClientChange}
                notFoundContent={
                  loading ? (
                    <div style={{ textAlign: "center", padding: "12px 0" }}>
                      <Spin size="small" />
                    </div>
                  ) : (
                    "No results found"
                  )
                }
              />
            </Form.Item>
          </Col>
        ) : (
          <>
            <Col xs={24} sm={12}>
              <Form.Item
                name="firstName"
                label={t("booking.labels.first_name")}
                rules={[{ required: true, message: t("booking.error_messages.first_required") }]}
              >
                <Input placeholder={t("booking.labels.first_name")} disabled={clientDetailDisabled} onKeyDown={handleNameKeyDown} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="lastName"
                label={t("booking.labels.last_name")}
                rules={[{ required: true, message: t("booking.error_messages.last_required") }]}
              >
                <Input
                  onKeyDown={handleLastNameKeyPress}
                  onInput={handleLastNameInput}
                  placeholder={t("booking.labels.last_name")}
                  disabled={clientDetailDisabled}
                  onBlur={(e) => handleLastNameBlur(e, form)}
                />
              </Form.Item>
            </Col>
          </>
        )}
        <Col xs={24} sm={12}>
          <Form.Item
            name="email"
            label={t("booking.labels.email")}
            dependencies={["phone_number"]}
            rules={[
              { type: "email", message: t("booking.error_messages.email_invalid") },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  const phone = getFieldValue("phone_number")
                  if (!value && !phone?.fullNumber) {
                    return Promise.reject(new Error(t("booking.error_messages.phone_or_email_required")))
                  }
                  if (emailError) {
                    return Promise.reject(new Error(emailError))
                  }
                  return Promise.resolve()
                }
              })
            ]}
          >
            <Input placeholder="example@email.com" disabled={clientDetailDisabled || isExistingClient} onChange={onEmailInputChange} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            name="phone_number"
            label={t("booking.labels.phone_number")}
            dependencies={["email"]}
            rules={[
              ({ getFieldValue }) => ({
                validator(_, value) {
                  const email = getFieldValue("email")
                  if (!value?.fullNumber && !email) {
                    return Promise.reject(new Error(t("booking.error_messages.phone_or_email_required")))
                  }
                  if (phoneError) {
                    return Promise.reject(new Error(phoneError))
                  }
                  if (value?.fullNumber) {
                    return validatePhoneNumber(value.fullNumber)
                  }
                  return Promise.resolve()
                }
              })
            ]}
          >
            <PhoneInput
              key={`phoneinput-${isExistingClient}`}
              disabled={phoneDisabled}
              initialValue={phone?.fullNumber}
              initialCountryCode={phone?.countryCode}
              onChange={(val) => {
                form.setFieldsValue({ phone_number: val })
                lastChangedField.current = "phone"
              }}
            />
          </Form.Item>
        </Col>
        {showNotice && (
          <Col xs={24} sm={24}>
            <AlreadyLinkedNotice
              form={form}
              singleMatchedClient={singleMatchedClient}
              singleMatchType={singleMatchType}
              conflictClients={conflictClients}
              onClientSelected={clearMatchedClients}
            />
          </Col>
        )}
      </Row>
    </>
  )
}

ClientDetail.propTypes = {
  form: PropTypes.object.isRequired,
  bookingId: PropTypes.string.isRequired,
  setAlertMessage: PropTypes.func.isRequired,
  setExistingClientId: PropTypes.func.isRequired
}

export default ClientDetail
